import { useLazyQuery } from '@apollo/client'
import {
  BodyText,
  CaptionText,
  Link,
  Button,
  DownloadIcon,
  HeadingText,
  Sheet,
  CriticalIcon,
} from '@paypalcorp/pp-react'
import Logo from 'ppbl/components/Logo'
import { navigate } from '@reach/router'
import DOMPurify from 'dompurify'
import serverData from 'ppbl/utils/server-data'
import { isEmpty, merge } from 'lodash-es'
import SpinnerAdapter from 'ppbl/components/SpinnerAdapter'
import clientLogger from 'ppbl/utils/client-logger'
import { trackClick, trackPage } from 'ppbl/utils/fpti-helper'
import { interpolate } from 'ppbl/utils/string-helper'
import { getCurrencyFormat, getLocalizedText } from 'ppbl/utils/locale-helper'
import React, {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { termsQuery } from '../../graphql/queries'
import {
  currencyParser,
  disclosurePayloadGenerator,
  getProductIdentifier,
  lbLogoUrl,
  isRenewalFlow,
  getPortalUrl,
  handleGraphqlError,
} from '../__utils__'
import { OfferContext } from './offerContext'
import './styles.css'
import { CaseTypes, discloaureModelConstants } from 'ppbl/utils/constants'

const fptiData = {
  page: 'disclosureModal',
}

type Props = {
  applicationType: string
  applicationId: string
  isSheetOpen: boolean
  setIsSheetOpen: Dispatch<SetStateAction<boolean>>
  buttonClassName?: string
  buttonDisabled?: boolean
  onClick?: (...args: any[]) => any
  createOfferLoading?: boolean
  acceptOfferLoading?: boolean
  portalAccess?: any
  loadRef?: any
}

const DisclosureModal: React.FC<Props> = ({
  applicationType,
  applicationId,
  isSheetOpen,
  onClick,
  createOfferLoading,
  acceptOfferLoading,
  setIsSheetOpen,
  buttonClassName,
  buttonDisabled,
  portalAccess,
  loadRef,
}) => {
  useEffect(() => {
    if (isSheetOpen) {
      trackPage(
        merge(fptiData, {
          applicationId,
        }),
      )
      sessionStorage.setItem('mostRecentCompletedPage', 'offer')
      clientLogger.info(
        'Disclosure Modal is opened - Application ID:',
        applicationId,
      )
    }
  }, [isSheetOpen])

  function formattedCurrency(value) {
    return getCurrencyFormat(Number(value))
  }

  const {
    offerValues,
    annualPercentageRate,
    averageMonthlyCost,
    disbursementAmount,
    termInDays,
    renewalStatus,
    previousLoans,
    previousLoanOutstandingAmount,
  } = useContext(OfferContext)

  const isPPBL = getProductIdentifier(applicationType) === 'PPBL_US'

  const disclosurePlaceHolders: any = {
    loanAmount: formattedCurrency(offerValues.offerAmount),
    apr: Number.parseFloat(annualPercentageRate),
    loanFee: formattedCurrency(offerValues.totalInterestCharge),
    repaymentAmt: formattedCurrency(
      offerValues.offerAmount + offerValues.financeCharges,
    ),
    avgMonthlyCost: averageMonthlyCost,
    paymentAmt: formattedCurrency(offerValues.weeklyPayment),
    term: Number.parseFloat(termInDays),
    disbursementAmt: disbursementAmount,
  }

  if (renewalStatus === 'LOAN_RENEWAL' && !isEmpty(previousLoans)) {
    const existingLoanAccountsAsString = previousLoans
      .map((obj) => obj.id)
      .join(', ')

    disclosurePlaceHolders.internalPyoffAmt = formattedCurrency(
      previousLoanOutstandingAmount,
    )
    disclosurePlaceHolders.existingAccountNumbers = existingLoanAccountsAsString
    disclosurePlaceHolders.amountFinanced = formattedCurrency(
      currencyParser(disbursementAmount) + previousLoanOutstandingAmount,
    )
  }

  const [getDisclosure, { loading, data, error }] = useLazyQuery(termsQuery, {
    variables: {
      termsInput: disclosurePayloadGenerator(
        applicationId,
        disclosurePlaceHolders,
      ),
      style: 'HTML',
    },
  })

  const initialRender = useRef(true)
  const [allLoaded, setAllLoaded] = useState(false)

  const renewalToken = serverData.startParams?.renewalToken

  const dirtyHTML =
    data?.credit?.businessCredit?.terms?.formattedContent?.content

  const sanitizedHTML = DOMPurify.sanitize(dirtyHTML, {
    FORCE_BODY: true,
    ADD_TAGS: ['style'],
  })

  useEffect(() => {
    if (error) {
      handleGraphqlError(
        error,
        'Error encountered while fetching disclosure',
        'DisclosureModal - getDisclosure',
        data?.applicationMetaData?.applicationId,
        renewalToken,
        data?.applicationMetaData?.legalBusinessName,
        applicationType,
      )
    }

    if (!initialRender.current && !acceptOfferLoading) {
      clientLogger.info(
        'Offer Disclosure Modal is loaded - Application ID:',
        applicationId,
      )
      getDisclosure()
      loadRef.current = loadRef.current + 1
    } else if (initialRender.current) {
      initialRender.current = false
    }
  }, [acceptOfferLoading, getDisclosure])

  useEffect(() => {
    if (!createOfferLoading && !acceptOfferLoading && !loading) {
      setAllLoaded(true)
    } else {
      setAllLoaded(false)
    }
  }, [createOfferLoading, acceptOfferLoading, loading])

  const openSheet = () => {
    trackClick(
      merge(fptiData, {
        component: 'openDisclosure_button',
        link: 'openDisclosure_button',
        applicationId,
      }),
    )
    setIsSheetOpen(true)
    onClick()
  }

  const closeSheet = () => {
    setIsSheetOpen(false)
  }

  const handleChoseAnotherOffer = () => {
    trackClick(
      merge(fptiData, {
        component: 'chooseAnotherOffer_button',
        link: 'chooseAnotherOffer_button',
        applicationId,
      }),
    )
    closeSheet()
  }

  const handleSubmit = () => {
    trackClick(
      merge(fptiData, {
        component: 'offerAccept_signAndContinue_button',
        link: 'offerAccept_signAndContinue_button',
        applicationId,
      }),
    )

    clientLogger.info(
      'Completed Disclosure Step - Application ID:',
      applicationId,
    )
    closeSheet()

    // navigat to portal in renewal flow
    if (isRenewalFlow(applicationType)) {
      const portalUrl = getPortalUrl(
        portalAccess,
        CaseTypes.PENDING_STIPULATIONS,
      )
      window.open(portalUrl, '_self')
    } else {
      navigate(
        `/merchant-lending/loanbuilder/application/us/${applicationType}/thank-you`,
      )
    }
  }

  const printElement = (element) => {
    const cloned = element.cloneNode(true)
    document.body.appendChild(cloned)
    cloned.classList.add('printable')
    window.print()
    document.body.removeChild(cloned)
  }

  const downloadDisclosure = () => {
    trackClick(
      merge(fptiData, {
        component: 'downloadDisclosure_button',
        link: 'downloadDisclosure_button',
        applicationId,
      }),
    )
    const printableHTML = document.getElementById('printableDisclosureContent')
    printElement(printableHTML)
  }

  const disclosureContent = () => {
    return (
      <>
        {!error ? (
          <div
            id="printableDisclosureContent"
            className="disclosureHTMLContent"
            dangerouslySetInnerHTML={{
              __html: sanitizedHTML,
            }}
          />
        ) : (
          <div
            className="disclosureErrorContent"
            data-testid="disclosureErrorContent"
          >
            <CriticalIcon
              className="errorIcon"
              size="xs"
              color="sysColorErrorMain"
            />
            <CaptionText>
              {loadRef && loadRef.current < 2
                ? interpolate(
                    getLocalizedText('offer-page.disclosure.retryText'),
                    {
                      break: <br />,
                    },
                  )
                : interpolate(
                    getLocalizedText('offer-page.disclosure.errorText.body'),
                    {
                      phone: (
                        <Link
                          href={discloaureModelConstants.phoneNumber.tel}
                          size="sm"
                          secondary
                        >
                          {discloaureModelConstants.phoneNumber.phone}
                        </Link>
                      ),
                    },
                  )}
            </CaptionText>
          </div>
        )}
      </>
    )
  }

  const FooterContents: React.FC = () => {
    return (
      <div className="disclosureFooter">
        {!error && (
          <>
            <Button
              className="button"
              disabled={!allLoaded}
              onClick={handleSubmit}
            >
              {getLocalizedText('offer-page.disclosure.footerPrimaryBtn')}
            </Button>
            <Button onClick={handleChoseAnotherOffer} tertiary>
              {getLocalizedText('offer-page.disclosure.footerSecondaryBtn')}
            </Button>
          </>
        )}
      </div>
    )
  }

  return (
    <div>
      <Button
        className={buttonClassName}
        onClick={openSheet}
        disabled={buttonDisabled}
        aria-haspopup="true"
        data-testid="show-offer-summary-button"
      >
        {getLocalizedText('offer-page.disclosure.showDisclosureBtn')}
      </Button>

      <Sheet
        id="disclosureModal"
        isOpen={isSheetOpen}
        onCloseComplete={closeSheet}
        containerClassName="disclosureContainer"
        title="Disclosure"
        showLogo={isPPBL ? true : false}
        action={
          !isPPBL && (
            <Logo
              data-testid="header-disclosure-logo"
              text="Loanbuilder"
              url={lbLogoUrl}
              className="lbLogoHeader"
              data-pa-click="Header-Disclosure-Logo-PayPal"
            />
          )
        }
        hideTitle={true}
        className="disclosureWrapper"
        mobileFixedHeight="90"
        footerContents={<FooterContents />}
        footerClassName="disclosureFooterContainer"
      >
        <HeadingText size="sm">
          {isPPBL
            ? getLocalizedText('offer-page.disclosure.headingText_PPBL')
            : getLocalizedText('offer-page.disclosure.headingText_LB')}
        </HeadingText>
        {!error && (
          <Button
            disabled={!allLoaded}
            onClick={downloadDisclosure}
            className="disclosureSection link"
            tertiary
            iconComponent={DownloadIcon}
          >
            {getLocalizedText('offer-page.disclosure.downloadBtn')}
          </Button>
        )}
        <div
          className={`disclosureSection ${
            error ? 'disclosureContentError' : 'disclosureContent'
          }`}
        >
          {allLoaded ? (
            disclosureContent()
          ) : (
            <div
              id="spinnerContainer-disclosure"
              data-testid="spinnerContainer-disclosure"
              className="disclosureSpinner"
            >
              <SpinnerAdapter />
            </div>
          )}
        </div>
        {!error ? (
          <BodyText className="disclosureSection" as="p">
            {isPPBL
              ? getLocalizedText('offer-page.disclosure.noteText_PPBL')
              : getLocalizedText('offer-page.disclosure.noteText_LB')}
          </BodyText>
        ) : (
          <div className="errorButtonSection">
            <Button
              className="button"
              onClick={() => {
                if (loadRef && loadRef.current < 2) {
                  handleChoseAnotherOffer()
                  openSheet()
                } else {
                  handleSubmit()
                }
              }}
            >
              {loadRef && loadRef.current < 2
                ? getLocalizedText('offer-page.disclosure.footerRetryButton')
                : getLocalizedText('offer-page.disclosure.footerErrorBtn')}
            </Button>
          </div>
        )}
      </Sheet>
    </div>
  )
}

export default DisclosureModal
